import { CommonModule } from '@angular/common';
import { ApplicationRef, Component, computed, ElementRef, inject, signal, Signal, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { InputDropdownConfig } from '@app/_interfaces/generic.interface';
import { LastUpdatedByService } from '@app/_services/last-updated-by.service';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { SettingsService } from '@app/_services/settings.service';
import { ViewSettingService } from '@app/_services/view-setting.service';
import { ViewSettingsStore, UserStore, PublicUserStore } from '@app/_stores';
import { AppThemeType, PolicyType, SUBSCRIPTION_TIER_STRINGS, SubscriptionEntitlement } from '@app/_types/generic.type';
import { getEntitlement, getSubscriptionState, getClosestSubEntitlement, isPremiumEntitlement } from '@app/_utils/effective-subscription';
import { toDate } from '@app/_services/date';
import { ThemeDialogComponent } from '@app/components/shared/dialogs/theme-dialog/theme-dialog.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { NotificationComponent } from '../notifications/notification/notification.component';
import { AnalyticsService } from '@app/_services/analytics.service';
import { Subject, takeUntil } from 'rxjs';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { KebabShowMenuComponent } from '@app/components/shared/kebab-show-menu/kebab-show-menu.component';
import { ModalService } from '@app/_services/modal.service';
import { MatMenuModule } from '@angular/material/menu';
import { ParseBytesPipe } from '@app/_pipes/parse-bytes.pipe';
import { AuthService } from '@app/_services/auth.service';
import { MapService } from '@app/_services/map.service';
import { ConsolidatedApiService } from '@app/_services/consolidated-api.service';
import { FirebaseService } from '@app/_services/firebase.service';
import { UserService } from '@app/_services/user.service';
import { DependencyService } from '@app/_services/dependency.service';
import { SubscriptionPlansComponent } from '../subscription/subscription-plans/subscription-plans.component';
import { PolicyDialogComponent } from '@app/components/shared/dialogs/policy-dialog/policy-dialog.component';
import { LegalDialogComponent } from '@app/components/shared/dialogs/legal-dialog/legal-dialog.component';
import { VersionDialogComponent } from '@app/components/shared/dialogs/version-dialog/version-dialog.component';
import { AccountDeleteComponent } from '../account/account-delete/account-delete.component';
import { ImportProgressDialogComponent } from '@app/components/shared/dialogs/import-progress-dialog/import-progress-dialog.component';
import { CloudSyncService } from '@app/_services/cloud-sync.service';
import { MatDialog } from '@angular/material/dialog';
import { log } from '@app/_services/logger';
import { ConnectivityService } from '@app/_services/connectivity.service';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

@Component({
    selector: 'app-app-settings',
    imports: [
        CommonModule,
        SvgComponent,
        NotificationComponent,
        KebabShowMenuComponent,
        MatMenuModule,
        ParseBytesPipe
    ],
    templateUrl: './app-settings.component.html',
    styleUrl: './app-settings.component.scss'
})

export class AppSettingsComponent implements OnInit, OnDestroy {

  @ViewChild('importBackupFileInput', { static: false }) importBackupFileInput!: ElementRef<HTMLInputElement>;
  readonly viewSettings = inject(ViewSettingsStore);
  readonly lastUpdatedByService = inject(LastUpdatedByService);
  readonly userStore = inject(UserStore);
  private readonly publicUserStore = inject(PublicUserStore);

  appThemeConfig: Signal<{ [key in AppThemeType]: InputDropdownConfig }> = computed(() => {
    return {
      systemDefault: {
        name: this.ss.t('themeSelect_systemDefault'),
      },
      dark: {
        name: this.ss.t('themeSelect_dark'),
      },
      light: {
        name: this.ss.t('themeSelect_light'),
      }
    }
  });

  getUtcDateString: Signal<string> = computed(() => {
    const d = this.cc.buildUpdatedDate();
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes} UTC`;
  });

  subscriptionPlanMap: Signal<Record<SubscriptionEntitlement, string>> = this.mapService.subscriptionEntitlementMap;

  subscriptionPlanName: Signal<string> = computed(() => {
    const eSub = this.ss.effectiveSubscription().eSubscriptionInfo;

    if (this.ss.isInGracePeriod()) {
      const closestEntitlement = getClosestSubEntitlement(eSub);
      return this.getPlanNameForEntitlement(closestEntitlement, eSub.subscriptionType, eSub.productId);
    }

    const effectiveEntitlement = getEntitlement(eSub);

    if (effectiveEntitlement === 'free' || !eSub.subscriptionStartDate) {
      return this.subscriptionPlanMap()['free'];
    }

    return this.getPlanNameForEntitlement(effectiveEntitlement, eSub.subscriptionType, eSub.productId);
  });

  effectiveStorageLimit: Signal<number> = computed(() => {
    const eSub = this.ss.effectiveSubscription().eSubscriptionInfo;
    const entitlement = this.ss.isInGracePeriod()
      ? getClosestSubEntitlement(eSub)
      : getEntitlement(eSub);
    return this.ss.storagePLan()[entitlement];
  });

  storageUsedPercentage: Signal<number> = computed(() => {
    const storageUsed = this.userStore.storageUsed();
    const storageLimit = this.effectiveStorageLimit();
    if (!storageLimit) return 0;
    return Math.floor((storageUsed / storageLimit) * 100);
  });

  subscriptionPlan: Signal<SubscriptionEntitlement> = computed(() => {
    return this.ss.entitlement();
  });

  subscriptionButtonText: Signal<string> = computed(() => {
    if (this.ss.isInGracePeriod()) {
      return this.ss.t('common_renew');
    }
    const entitlement = this.subscriptionPlan();
    if (entitlement === 'free') {
      return this.ss.t('common_upgrade') || 'UPGRADE';
    }
    return this.ss.t('account_subscriptionButtonManage') || 'MANAGE';
  });

  gracePeriodDaysRemaining: Signal<string> = computed(() => {
    const gracePeriodEndDate = this.ss.gracePeriodEndDate();
    if (!gracePeriodEndDate) return '';
    const now = Date.now();
    const daysRemaining = Math.max(0, Math.ceil((gracePeriodEndDate - now) / (1000 * 60 * 60 * 24)));
    return this.ss.t('subscription_deletedInDays', { days: daysRemaining.toString() });
  });

  subscriptionRenewsOnText: Signal<string> = computed(() => {
    const eSub = this.ss.effectiveSubscription().eSubscriptionInfo;
    const eState = getSubscriptionState(eSub);
    const eEntitlement = getEntitlement(eSub);

    if (eState === 'subscriptionExpired' || eState === 'none') return '';
    if (eEntitlement === 'free') return '';

    const expDateObj = toDate(eSub.subscriptionExpDate);
    if (!expDateObj) return '';
    if (expDateObj.getTime() <= Date.now()) return '';

    const formattedDate = this.ss.getFormattedDate(expDateObj, 'd MMM yyyy');

    if (eSub.unsubscribedAt && expDateObj.getTime() > Date.now()) {
      return this.ss.t('subscription_currentPlanExpiringContent', { date: formattedDate });
    }

    return this.ss.t('subscription_currentPlanRenewingInfo', { date: formattedDate });
  });

  backupDateText: Signal<string> = computed(() => {
    const exportLastUpdatedAt = this.userStore.exportLastUpdatedAt();
    if (!exportLastUpdatedAt) return '';
    const timeAgo = this.lastUpdatedByService.getTimeAgo(exportLastUpdatedAt);
    return `${this.ss.t('account_lastRequested')} ${timeAgo}`;
  });

  subscriptionExpDate: Signal<Date | null> = computed(() => {
    return this.userStore.subscriptionExpDate() || null;
  });

  screenEnterTime: Signal<Date> = signal(new Date());
  unSubscribe = new Subject<void>();

  constructor(
    public cc: CacheService,
    public ss: SettingsService,
    public vs: ViewSettingService,
    private modalService: ModalService,
    private alertService: AlertService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private authService: AuthService,
    private mapService: MapService,
    private consolidatedApi: ConsolidatedApiService,
    private fbService: FirebaseService,
    private userService: UserService,
    public ds: DependencyService,
    private appRef: ApplicationRef,
    private cloudSyncService: CloudSyncService,
    public dialog: MatDialog,
    private router: Router,
    private connectivityService: ConnectivityService,
  ) {

  }

  ngOnInit() {
    this.analyticsInit();
    this.publicUserStore.reconcileStorage();
  }

  navigateToToday() {
    this.router.navigate(['/today']);
  }

  weekStartDayLabel = computed(() => {
    const day = this.ss.effectiveWeekStartDay();
    const locale = this.ss.appLanguageCode();
    const now = new Date();
    const currentDay = now.getDay();
    const diff = (day - currentDay + 7) % 7;
    const refDate = new Date(now);
    refDate.setDate(now.getDate() + (diff === 0 ? 0 : diff));
    const label = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(refDate);
    return label.charAt(0).toUpperCase() + label.slice(1);
  });

  // ---- Experience methods ----

  openThemeDialog() {
    this.modalService.openModalLevel4(ThemeDialogComponent);
  }

  updateTheme() {
    this.ss.updateView();
    const themeColor = this.ss.themeColor();
    const mode = this.ss.mode();
    const capitalizedMode = mode.charAt(0).toUpperCase() + mode.slice(1);
    this.as.userPreferenceEvent('update', 'theme', mode);
  }

  // ---- Account methods ----

  editUserName() {
    this.userService.updateUserName();
  }

  // ---- Subscription methods ----

  private getPlanNameForEntitlement(
    entitlement: SubscriptionEntitlement,
    subscriptionType: string | null,
    productId: string | null,
  ): string {
    if (entitlement === 'free') return this.subscriptionPlanMap()['free'];

    const isMonthly = subscriptionType === 'monthly' || (productId?.includes('monthly') ?? false);

    if (entitlement === 'legend') {
      return isMonthly
        ? this.ss.t('account_subscriptionStatusLegendMonthly')
        : this.ss.t('account_subscriptionStatusLegendAnnual');
    }
    if (entitlement === 'pro') {
      return isMonthly
        ? (this.ss.t('account_subscriptionStatusProMonthly') || SUBSCRIPTION_TIER_STRINGS.pro.monthly)
        : (this.ss.t('account_subscriptionStatusProAnnual') || SUBSCRIPTION_TIER_STRINGS.pro.annual);
    }

    return this.subscriptionPlanMap()[entitlement];
  }

  openPlans() {
    this.modalService.openLevel3(SubscriptionPlansComponent);
  }

  async openAttachmentsGallery() {
    const { AttachmentComponent } = await import('@app/components/addons/attachments/attachment/attachment.component');
    this.modalService.openLevel2(AttachmentComponent, {}, { disableClose: true });
  }

  async openMessageAttachmentsBrowser() {
    const { MessageAttachmentBrowserComponent } = await import('@app/components/addons/attachments/message-attachment-browser/message-attachment-browser.component');
    this.modalService.openLevel2(MessageAttachmentBrowserComponent, { source: 'account_settings' }, { disableClose: true });
  }

  // ---- Data methods ----

  async backupData() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('backupDataConfirmation_title'),
      description: this.ss.t('backupDataConfirmation_content1'),
      yesButtonText: this.ss.t('common_proceed'),
      noButtonText: this.ss.t('common_buttonCancel'),
      secondMessage: this.cc.user.email,
      secondMessageClass: 'color-8 text-center',
      thirdMessage: this.ss.t('backupDataConfirmation_content3'),
    });
    if (!res) return;
    const funcStartTime = new Date();

    this.alertService.toast('backupDataRequestSent_content');

    this.userStore.updateSettings({
      exportStatus: 'inProgress',
      exportLastUpdatedAt: new Date()
    });

    try {
      const result = await this.consolidatedApi.call('createAccountBackupFile', {
        'uid': this.cc.user.uid,
        'localTime': new Date().toISOString(),
        'tz': new Date().getTimezoneOffset(),
      });
      if (result.error) {
        throw new Error(result.error);
      }
      log.info('Settings', 'Backup response', result.data);

      this.as.dataActionEvent('dataExport', funcStartTime);
    } catch (error) {
      log.error('Settings', 'Backup error', error);

      this.userStore.updateSettings({
        exportStatus: 'failed',
        exportLastUpdatedAt: new Date()
      });
    }
  }

  async importData() {
    if (!this.ss.isPremiumUser()) {
      this.ds.featureUnavailable(!this.subscriptionExpDate());
      return;
    }

    // Pre-flight connectivity check (matches Flutter's UtilityMethods.showOfflineDialogOrPerformAction)
    if (!this.connectivityService.isOnline()) {
      await this.alertService.alert(
        this.ss.t('noInternetConnection_title'),
        this.ss.t('noInternetConnection_content'),
        this.ss.t('common_ok')
      );
      return;
    }

    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('importDataConfirmation_title'),
      description: this.ss.t('importDataConfirmation_content'),
      yesButtonText: this.ss.t('importDataConfirmation_import'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    this.importBackupFileInput.nativeElement.click();
  }

  async importFile(event: Event): Promise<void> {
    const funcStartTime = new Date();
    const fileList: FileList | null = (event.target as HTMLInputElement).files;
    if (fileList && fileList.length > 0) {
      const backupFile = fileList[0];
      const extension = backupFile.name.split('.').pop()?.toLowerCase();
      const fileSize = backupFile.size;

      if (extension !== 'zip') {
        this.alertService.alert(
          this.ss.t('invalidImportFileFormat_title'),
          this.ss.t('invalidImportFileFormat_content'),
          this.ss.t('common_ok')
        );
        return;
      }

      if (fileSize >= 1000 * 1000 * 1000) {
        this.alertService.alert(
          this.ss.t('importFileTooLarge_title'),
          this.ss.t('importFileTooLarge_content'),
          this.ss.t('common_ok')
        );
        return;
      }

      let uploadCancelled = false;

      const progressDialogRef = this.dialog.open(ImportProgressDialogComponent, {
        width: '100%',
        maxWidth: '500px',
        disableClose: true,
        data: {
          fileName: backupFile.name,
          fileSize: backupFile.size,
          onCancel: () => {
            uploadCancelled = true;
          }
        }
      });

      const progressDialog = progressDialogRef.componentInstance;

      const dialogCloseSubscription = progressDialogRef.afterClosed().subscribe(result => {
        if (result?.cancelled) {
          uploadCancelled = true;
        }
        if (result?.timeout) {
          log.warn('Settings', 'Import timeout - no progress for 60 seconds');
        }
      });

      try {
        const uploadRes = await this.fbService.importBackupFile(
          backupFile,
          (progress: number) => {
            if (!uploadCancelled) {
              progressDialog.updateProgress(progress);
            }
          }
        );

        if (uploadCancelled) {
          return;
        }

        log.info('Settings', 'Import upload result', uploadRes);

        if (uploadRes.success) {
          progressDialogRef.close();

          this.alertService.alert(
            this.ss.t('dataUploadingProcess_title'),
            this.ss.t('dataUploadingProcess_completedInfo'),
            this.ss.t('common_ok'),
            'color-35',
            null,
            null,
            'success'
          );

          this.as.dataActionEvent('dataImport', funcStartTime);
        } else {
          progressDialogRef.close();
        }
      } catch (error) {
        log.error('Settings', 'Import error', error);
      } finally {
        dialogCloseSubscription.unsubscribe();
        this.importBackupFileInput.nativeElement.value = '';
      }
    }
  }

  async deleteData() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('deleteDataConfirmation_title'),
      description: this.ss.t('deleteDataConfirmation_content'),
      yesButtonText: this.ss.t('account_deleteData'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
      isActionButtonsInCol: true,
      additionalButtonText: this.ss.t('deleteDataConfirmation_backupData'),
      additionalButtonColor: 'color-35',
    });

    if (!res) return;
    if (res === 'ADDITIONAL_ACTION') {
      this.backupData();
      return;
    }

    if (res) {
      const confirm = await this.alertService.confirmRevamped({
        title: this.ss.t('deleteDataAlert_title'),
        description: this.ss.t('deleteDataAlert_content'),
        yesButtonText: this.ss.t('account_deleteData'),
        noButtonText: this.ss.t('common_buttonCancel'),
        yesButtonColor: 'color-11',
        noButtonColor: 'color-7',
      });
      if (!confirm) return;
    }

    const funcStartTime = new Date();
    try {
      this.cc.isLoading.set(true);
      const result = await this.consolidatedApi.call<{ result: string }>('deleteData', {
        'uid': this.cc.user.uid,
        'localTime': new Date().toISOString(),
        'tz': new Date().getTimezoneOffset(),
      });
      if (result.error) {
        throw new Error(result.error);
      }
      log.info('Settings', 'Delete data response', result.data);
      if (result.data?.result === 'success') {
        this.as.dataActionEvent('dataDelete', funcStartTime);
        this.authService.logoutUser();
      }
    } catch (error) {
      log.error('Settings', 'Delete data error', error);
    } finally {
      this.cc.isLoading.set(false);
    }
  }

  // ---- Legal / About methods ----

  openPolicy(policyType: PolicyType) {
    const data = {
      type: policyType,
    };

    if (this.cc.isNarrowScreen()) {
      this.modalService.openMainContent(
        PolicyDialogComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    } else {
      this.modalService.openLevel1(
        PolicyDialogComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    }
  }

  openVersionDetails() {
    this.modalService.openModalLevel4(VersionDialogComponent);
  }

  openLegal() {
    this.modalService.openLevel3(LegalDialogComponent, {}, {
      disableClose: true
    });
  }

  // ---- Logout / Delete Account methods ----

  async logout() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('logoutThisDeviceConfirmation_title'),
      description: this.ss.t('logoutThisDeviceConfirmation_content', { brandName: BRAND_CONFIG.name }),
      yesButtonText: this.ss.t('common_buttonLogOut'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;

    if (this.authService.hasPendingUploads()) {
      const uploadCount = this.authService.getActiveUploadsCount();
      const title = this.ss.t('pendingUploads_title');
      const description = uploadCount === 1
        ? this.ss.t('pendingUploads_logoutWarningContentSingle')
        : this.ss.t('pendingUploads_logoutWarningContentMultiple', { count: uploadCount.toString() });

      const uploadWarningRes = await this.alertService.confirmRevamped({
        title: title,
        description: description,
        yesButtonText: this.ss.t('common_buttonLogoutAnyway'),
        noButtonText: this.ss.t('common_buttonCancel'),
        yesButtonColor: 'color-35',
        noButtonColor: 'color-7',
        status: 'warning',
      });

      if (!uploadWarningRes) return;
    }

    if (this.cloudSyncService.hasPendingSyncData()) {
      const syncCount = this.cloudSyncService.getPendingSyncCount();
      const title = this.ss.t('pendingChanges_title');
      const description = this.ss.t('pendingChanges_content', { count: syncCount });

      const syncWarningRes = await this.alertService.confirmRevamped({
        title: title,
        description: description,
        yesButtonText: this.ss.t('common_buttonLogoutAnyway'),
        noButtonText: this.ss.t('common_buttonCancel'),
        yesButtonColor: 'color-35',
        noButtonColor: 'color-7',
        status: 'warning',
      });

      if (!syncWarningRes) return;
    }

    this.cc.isLoading.set(true);
    await this.authService.logoutUser();
  }

  async logoutAllDevices() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('logoutAllDeviceConfirmation_title'),
      description: this.ss.t('logoutAllDeviceConfirmation_content', { brandName: BRAND_CONFIG.name }),
      yesButtonText: this.ss.t('common_buttonLogOut'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    this.cc.isLoading.set(true);
    this.ss.logoutAllDevices();
  }

  async deleteAccount() {
    const isNarrow = this.cc.isNarrowScreen();

    if (isNarrow) {
      this.modalService.openMainContent(AccountDeleteComponent, {});
    } else {
      this.modalService.openLevel1(AccountDeleteComponent, {});
    }
  }

  // ---- Analytics ----

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('drawerSettings', previousScreen);
        this.storageService.setCurrentScreen('drawerSettings');
      }
    });
  }

  ngOnDestroy() {
    this.as.screenExitEvent('drawerSettings', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
